import React, { useState, useEffect, Fragment } from "react";
import SimpleLabelValue from "../../components/SimpleLabelValue";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import httpService from "../../common/httpService";
import "./style.scss";
import { useAuth } from "../../common/AuthContext";
import { backendUrlImages } from "../../constant/config";
import { Button, Grid, Typography, Paper, CircularProgress } from "@mui/material";

// ✅ Auto nights calculation function
const calculateTotalNights = (checkIn, checkOut) => {
  if (!checkIn || !checkOut) return 0;
  const checkInDate = new Date(checkIn);
  const checkOutDate = new Date(checkOut);
  const timeDiff = checkOutDate.getTime() - checkInDate.getTime();
  const nights = Math.ceil(timeDiff / (1000 * 3600 * 24));
  return nights > 0 ? nights : 0;
};

const ReservationDetails = ({ setDisabledButton, onPayNow, loading }) => {
  const [reservations, setReservations] = useState([]);
  const BASE_URL = process.env.REACT_APP_BASE_URL;
  const { myConferenceId } = useAuth();

  const fetchReservations = async () => {
    if (!myConferenceId) return;
    try {
      const token = localStorage.getItem("token");
      if (!token) throw new Error("Authorization token is missing.");

      const response = await httpService({
        method: "GET",
        url: `${BASE_URL}/reservations/rooms/${myConferenceId}`,
        headers: { Authorization: `Bearer ${token}` },
        withToast: false,
        showLoader: true,
      });

      if (response?.status === "success" && response?.data) {
        const formattedReservations = formatReservationData(response.data);
        setReservations(formattedReservations);

        if (response.data.length > 0) {
          setDisabledButton(false);
        } else {
          setDisabledButton(true);
        }
      } else {
        setDisabledButton(true);
      }
    } catch (error) {
      console.error("Fetch Reservations Error:", error);
      setDisabledButton(true);
    }
  };

  useEffect(() => {
    fetchReservations();
  }, [myConferenceId]);

  const formatReservationData = (data) =>
    data.map((reservation) => ({
      id: reservation.id,
      userId: reservation.user_id,
      roomCount: reservation.room_count,
      companionsCount: reservation.companions_count,
      companionsNames: reservation.companions_names || "-",
      updateDeadline: reservation.update_deadline,
      conferenceId: reservation.conference_id,
      createdAt: reservation.created_at,
      updatedAt: reservation.updated_at,
      rooms: reservation.rooms.map((room) => {
        const totalNights = room.total_nights || calculateTotalNights(room.check_in_date, room.check_out_date);
        return {
          ...room,
          id: room.id,
          type: room.room_type,
          occupant: room.occupant_name || "-",
          checkIn: room.check_in_date,
          checkOut: room.check_out_date,
          totalNights: totalNights,
          cost: room.cost,
          additionalCost: room.additional_cost,
          isConfirmed: room.is_confirmed ? "Yes" : "No",
          reservation_invoices: room.reservation_invoices || [],
        };
      }),
    }));

  // 🔥 Calculate TOTAL from ALL invoices across ALL rooms
  const calculateGrandTotal = () => {
    let grandTotal = 0;
    reservations.forEach((reservation) => {
      reservation.rooms.forEach((room) => {
        room.reservation_invoices?.forEach((invoice) => {
          grandTotal += parseFloat(invoice.total || 0);
        });
      });
    });
    return grandTotal;
  };

  const RoomDetails = ({ room }) => (
    <div>
      <div className="room-details">
        <SimpleLabelValue label="Occupant" value={room.occupant} />
        <SimpleLabelValue label="Type" value={room.type} />
        <SimpleLabelValue label="Arrival Date" value={room.checkIn || "-"} />
        <SimpleLabelValue label="Departure Date" value={room.checkOut || "-"} />
        <SimpleLabelValue label="Total Nights" value={room.totalNights || "-"} />
      </div>

      <div className="line"></div>

      <div className="invoice-details">
        {room.reservation_invoices.length > 0 && (
          <div>
            <div className="title-reservation">Invoice Details</div>
            {room.reservation_invoices.map((invoice) => (
              <div key={invoice.id} className="reservation-invoice">
                <SimpleLabelValue label="Price" value={`$${invoice.price}`} />
                <SimpleLabelValue label="Additional Price" value={`$${invoice.additional_price}`} />
                <SimpleLabelValue label="Total" value={`$${invoice.total}`} />
                <SimpleLabelValue
                  label="Confirmation PDF"
                  value={
                    invoice.confirmationPDF ? (
                      <a
                        href={`${backendUrlImages}${invoice.confirmationPDF}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        View PDF
                      </a>
                    ) : (
                      "-"
                    )
                  }
                />
                <SimpleLabelValue label="Status" value={invoice.status} />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  const ReservationCard = ({ reservation }) => (
    <div className="reservation-card-section">
      <div className="title-reservation">Reservation Details</div>
      <div className="info-reservation-section">
        <SimpleLabelValue label="Room Count" value={reservation.roomCount} />
        <SimpleLabelValue
          label="Update Deadline"
          value={reservation.updateDeadline || "-"}
        />
      </div>

      {reservation.rooms.map((room, index) => (
        <Fragment key={room.id}>
          <div className="title-reservation">Room {index + 1}</div>
          <RoomDetails room={room} />
        </Fragment>
      ))}
    </div>
  );

  return (
    <div className="reservation-details">
      {/* 🔥 GRAND TOTAL PAYMENT BUTTON WITH API */}
      {reservations.length > 0 && (
        <Paper 
          elevation={3}
          sx={{ 
            p: 3, 
            mb: 3,
            background: "linear-gradient(135deg, #9B1321 0%, #B22222 100%)",
            color: "white",
            borderRadius: "12px",
            textAlign: "center"
          }}
        >
          <Typography variant="h5" fontWeight="bold" gutterBottom>
            Grand Total
          </Typography>
          <Typography variant="h3" fontWeight="bold" gutterBottom>
            ${calculateGrandTotal().toLocaleString()}
          </Typography>
          <Button
            variant="contained"
            size="large"
            onClick={onPayNow}
            disabled={loading}
            sx={{
              backgroundColor: "#FFD700",
              color: "#000",
              fontWeight: "bold",
              fontSize: "1.2rem",
              padding: "12px 40px",
              borderRadius: "25px",
              mt: 2,
              boxShadow: "0 4px 20px rgba(255,215,0,0.4)"
            }}
          >
            {loading ? <CircularProgress size={24} color="inherit" sx={{color: '#000'}} /> : '💳 Pay Now'}
          </Button>
        </Paper>
      )}

      {reservations.length > 0 ? (
        reservations.map((reservation) => (
          <ReservationCard key={reservation.id} reservation={reservation} />
        ))
      ) : (
        <Typography sx={{ textAlign: "center", color: "#555", marginTop: "20px" }}>
          No reservation details found.
        </Typography>
      )}
    </div>
  );
};

const Reservation = () => {
  const navigate = useNavigate();
  const [disabledBtn, setDisabledButton] = useState(true);
  const [loading, setLoading] = useState(false);

  // 🔥 FIXED API Payment Handler - Handles approval_url
  const handlePayAllNow = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      
      // 🔥 Get REAL grand total - Direct calculation (no DOM scraping)
      const grandTotalElement = document.querySelector('.reservation-details .MuiTypography-h3');
      const grandTotalText = grandTotalElement?.textContent?.replace(/[^\d.]/g, '') || '1250';
      const grandTotal = parseFloat(grandTotalText);

      console.log("💰 Paying amount:", grandTotal);

      // 🔥 Hit Payment API
      const response = await fetch("https://eventscons.com/eventbackendnew/api/create-payment", {
        method: "POST",
        headers: {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          amount: grandTotal,
          return_url: `${window.location.origin}/reservation`,
          cancel_url: `${window.location.origin}/reservation`,
        })
      });

      const result = await response.json();
      console.log("✅ Payment API Response:", result);

      if (response.ok) {
        // 🔥 PERFECT: Handle approval_url (PayPal) FIRST
        if (result.approval_url) {
          console.log("🔗 Redirecting to PayPal:", result.approval_url);
          window.location.href = result.approval_url; // Direct to PayPal
        } 
        // Fallbacks
        else if (result.redirect_url) {
          window.location.href = result.redirect_url;
        } 
        else if (result.payment_url) {
          window.open(result.payment_url, '_blank');
        } 
        else {
          alert(`✅ ${result.message || 'Payment created!'}`);
        }
      } else {
        alert(`❌ Error: ${result.message || 'Payment failed'}`);
      }
    } catch (error) {
      console.error("Payment Error:", error);
      alert("❌ Payment failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="all-reservation-form">
      <Grid container direction="column" spacing={2}>
        <Grid item xs={12}>
          <Typography
            variant="h6"
            sx={{
              color: "#9B1321",
              fontWeight: "bold",
              fontSize: { xs: "1.3rem", sm: "2rem", md: "2rem" },
              textAlign: "center",
            }}
            className="title-container"
          >
            Reservation Information Page
          </Typography>
        </Grid>

        <Grid item xs={12}>
          <Grid container spacing={2} justifyContent="center" sx={{ padding: "5px" }}>
            <Grid item xs={12} sm={6} md={3}>
              <Button
                variant="contained"
                color="primary"
                className="reservation-information-btn"
                disabled={!disabledBtn}
                onClick={() => navigate("/stepper")}
                fullWidth
              >
                Add Reservation Information
              </Button>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Button
                variant="contained"
                sx={{ backgroundColor: "#9B1321" }}
                className="reservation-information-btn"
                disabled={disabledBtn}
                onClick={() => navigate("/stepper/edit")}
                fullWidth
              >
                Edit Reservation Information
              </Button>
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      <ReservationDetails 
        setDisabledButton={setDisabledButton} 
        onPayNow={handlePayAllNow}
        loading={loading}  // ✅ FIXED: Pass loading prop
      />
    </div>
  );
};

export default Reservation;
